import { BrowserSupport } from '@tableau/browser-support';
import DesignTokens from '@tableau/design-tokens';
import { TableauFileViewBaseIcon } from '@tableau/icons/TableauFileViewBaseIcon';
import { TableauFileWorkbookBaseIcon } from '@tableau/icons/TableauFileWorkbookBaseIcon';
import { ColorService } from '@tableau/vizportalclient-common-components';
import Axios, { AxiosResponse, CancelTokenSource } from 'axios';
import React from 'react';
import { classes } from 'typestyle';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { ContentClickedEventHandler } from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '~commonInfra/telemetryEvents/util/EventHandlerFactories';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { CardResizerService } from '~src/spacesPlatform/services/CardResizerService';
import { generateUniqueId } from '~src/spacesPlatform/utils/DomUtils';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { CardViewStyles, TOTAL_CARD_WIDTH } from './CardViewStyles';

export const ThumbnailTestId = 'thumbnail';
export const debounceDuration = 300;
const cssPrefix = 'Thumbnail';

// Lowercased form of the server's `ViewerControlledVizTheme.DEFAULT` enum name. Session info serializes
// this preference by enum name (e.g. 'DEFAULT' | 'DARK'), like the sibling `viewerControlledTheme`. When
// the viewer's viz area is Default, the thumbnail image renders light even under dark chrome, so the dark
// watermark overlay is suppressed to avoid a dark badge over a light image.
const VIZ_THEME_DEFAULT = 'default';

export type ThumbnailProps = Pick<React.AriaAttributes, 'aria-label'> & {
  /** The url to navigate to when clicking the thumbnail; the thumbnail will have a link element only if this is present */
  readonly navigateUrl?: string;
  /** Optional callback fired on mousedown, before navigation occurs */
  readonly onNavigate?: () => void;
  /** The default image url, used when `thumbnailImageUrl` is not present or fails to load */
  readonly defaultImageUrl: string;
  /**
   * Optional React node rendered centered inside the thumbnail instead of the CSS `defaultImageUrl`
   * background. Used to render a reactive @tableau/icons Spot illustration in place of a static svg
   * data-uri. When present, `defaultImageUrl` is ignored for the placeholder (but a real
   * `thumbnailImageUrl`, once loaded, still wins and renders via background).
   */
  readonly defaultImageNode?: React.ReactNode;
  /** Whether the link dragging should be disabled or not */
  readonly allowDragAndDrop: boolean;
  /** Whether the `navigateUrl` should be opened in a new tab or current tab */
  readonly openLinkInNewTab?: boolean;
  /** The image url */
  readonly thumbnailImageUrl?: string;
  /** Whether or not to set `background-size: cover` when styling the thumbnail */
  readonly imageCoverBackground?: boolean;
  /** Whether the thumbnail should look like a stack of cards (useful for workbook thumbnail) or just one card */
  readonly decorate?: boolean;
  /** Whether or not to show hover styling on the thumbnail */
  readonly hovered?: boolean;
  /** Whether or not to show selection styling on the thumbnail */
  readonly selected?: boolean;
  /** Whether the `thumbnailImageUrl` should be treated as a base64 encoded svg data URI instead of an actual image url */
  readonly useBase64EncodedImage?: boolean;
  /** Height override for the thumbnail; if not present then the height defaults to 184px specified in `CardViewStyles` */
  readonly customHeight?: number;
  /** Text to be rendered at the center of thumbnail */
  readonly centeredText?: string;
  /**
   * Props for making thumbnail a custom SVG shape. For example, the project card is in the shape of 
   * a folder. If you provide `CustomShape` props, `Thumbnail` will take care of making the content of your thumbnail
   * the desired shape, and cropping the background image and hover/selection overlay to match the shape. Applies
   * special styles for the focus indicator so that it will properly follow the contours of the shape,
   * rather than being cut off. 
   * 
   * NOTE: Use `makeCustomThumbnailShape` helper to generate `CustomShape` props 
   */
  readonly customShape?: CustomShape;

  /** Drives the dark-mode watermark's icon and label (workbook / view). Omit to disable the watermark. */
  readonly contentType?: ExplorableItemType;
}

export interface ThumbnailState {
  readonly loadThumbnailImage: boolean;
  cardZoomPercentage: number;
  theme: string;
  // Optional: absent when the session user is not loaded, or when the server predates this field.
  vizTheme?: string;
}

const CARD_STACK_DECORATION = <div className={CardViewStyles.ThumbnailWorkbookDecoration} />;

export class Thumbnail extends React.Component<ThumbnailProps, ThumbnailState> {
  private unsubscribeCardResizerListener: () => void;
  public static thumbnailCache: { [url: string]: string } = {};

  public thumbnailImage: HTMLImageElement;
  public cancelTokenSource: CancelTokenSource;

  private thumbnailImageBase64Data: string;
  private debounceTimer: NodeJS.Timer;

  private unsubscribeThemeChanged: () => void = () => { };

  constructor(props: ThumbnailProps) {
    super(props);
    this.state = {
      // Snapshot the viewer's persisted viz-area theme once. Unlike `theme` (chrome) below, this is NOT kept
      // live. That is usually safe: the only UI that changes it lives on the account-settings route, which
      // unmounts these cards, so they re-read it on remount. It is not fully airtight though — a background
      // session refresh (window focus / session ping -> refreshSessionInfo) or a cross-tab save can update the
      // redux value in place while cards stay mounted, leaving a stale watermark until the next remount. That
      // window is cosmetic and only reachable once viewerControlledThemes_VizAreaEnabled ships; making this
      // live via a store subscription (mirroring `unsubscribeThemeChanged`) is a follow-up. May be undefined
      // (no session user yet / older server) — see usage.
      vizTheme: SessionUserSelectors.viewerControlledVizTheme(getAppStore().getState()),
      loadThumbnailImage: false,
      cardZoomPercentage: CardResizerService.instance.getCardZoomPercentage(),
      theme: ColorService.instance.getTheme(),
    };
    this.thumbnailImage = new Image();
  }

  public componentDidMount() {
    if (this.props.useBase64EncodedImage) {
      if (!Thumbnail.thumbnailCache[this.props.thumbnailImageUrl]) {
        const CancelToken = Axios.CancelToken;
        this.cancelTokenSource = CancelToken.source();

        this.debounceTimer = setTimeout(() => {
          Axios.get(this.props.thumbnailImageUrl, { cancelToken: this.cancelTokenSource.token })
            .then((result: AxiosResponse) => {
              this.thumbnailImageBase64Data = `data:image/svg+xml;base64,${this.sanitizeEncodedSvg(result.data)}`;
              Thumbnail.thumbnailCache[this.props.thumbnailImageUrl] = this.thumbnailImageBase64Data;
              this.setState({ loadThumbnailImage: true });
            }, error => {
            });
        }, debounceDuration);
      } else {
        this.thumbnailImageBase64Data = Thumbnail.thumbnailCache[this.props.thumbnailImageUrl];
        this.setState({ loadThumbnailImage: true });
      }
    } else if (BrowserSupport.IsSafari()) {
      // Safari doesn't support aborting the pending image requests by setting the image src to empty.
      // So we need to do it differently in Safari by using ajax call to download images.
      // The reason that we don't use ajax call to download images in other browsers (e.g Chrome, Firefox) is that
      // Using ajax call to download images is obviously slower than using <img> element. But it's not slower in Safari.
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel();
      }
      if (this.props.thumbnailImageUrl) {
        if (!Thumbnail.thumbnailCache[this.props.thumbnailImageUrl]) {
          const CancelToken = Axios.CancelToken;
          this.cancelTokenSource = CancelToken.source();

          this.debounceTimer = setTimeout(() => {
            Axios.get(this.props.thumbnailImageUrl, { cancelToken: this.cancelTokenSource.token, responseType: 'arraybuffer' })
              .then((result: AxiosResponse) => {
                this.thumbnailImageBase64Data = `data:image/png;base64,${Buffer.from(result.data, 'binary').toString('base64')}`;
                Thumbnail.thumbnailCache[this.props.thumbnailImageUrl] = this.thumbnailImageBase64Data;
                this.setState({ loadThumbnailImage: true });
              }, error => {
              });
          }, debounceDuration);
        } else {
          this.thumbnailImageBase64Data = Thumbnail.thumbnailCache[this.props.thumbnailImageUrl];
          this.setState({ loadThumbnailImage: true });
        }
      }
    } else {
      if (!Thumbnail.thumbnailCache[this.props.thumbnailImageUrl]) {
        if (this.props.thumbnailImageUrl) {
          this.debounceTimer = setTimeout(() => {
            this.thumbnailImage.src = this.props.thumbnailImageUrl;
            this.thumbnailImage.onload = () => {
              this.setState({ loadThumbnailImage: true });
              Thumbnail.thumbnailCache[this.props.thumbnailImageUrl] = this.props.thumbnailImageUrl;
            };
          }, debounceDuration);
        }
      } else {
        this.setState({ loadThumbnailImage: true });
      }
    }

    this.unsubscribeCardResizerListener = CardResizerService.instance.registerListener(cardZoomPercentage => {
      this.setState({ cardZoomPercentage: cardZoomPercentage });
    });

    this.unsubscribeThemeChanged = ColorService.instance.globalContainer.themeChanged.listenTo(theme => this.setState({ theme }));
  }

  private sanitizeEncodedSvg(encodedSvg: string): string {
    // remove xlink:href attribute
    let sanitized = atob(encodedSvg).replace(/xlink:href=\"[^\"]*\"/ig, '');
    // Remove doctype
    sanitized = sanitized.replace(/<!DOCTYPE[\S\s]*]/ig, '');
    return btoa(sanitized);
  }

  public componentWillUnmount() {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel();
    }
    this.thumbnailImage.src = '';
    this.thumbnailImage.onload = null;
    this.unsubscribeCardResizerListener();
    this.unsubscribeThemeChanged();
    clearTimeout(this.debounceTimer);
  }

  private buildThumbnailOverlayDiv(): JSX.Element {
    return (
      <div data-tb-test-id={'thumbnail-overlay'}
        className={classes(CardViewStyles.SelectOverlay, this.props.customShape?.clippingClass)}
        style={{ height: this.props.customHeight }}>
      </div>
    );
  }

  private buildDarkModeOverlay(): JSX.Element {
    // The watermark relies on `position: relative` on the outer wrapper (see `outerThumbnailStyle`
    // in render()), which is only applied on the non-customShape path. Bail early so future callers
    // that combine customShape with a mapped contentType don't render the watermark in the wrong place.
    if (this.props.customShape) {
      return <></>;
    }

    let icon: JSX.Element | undefined = undefined;
    let text: string | undefined = undefined;

    if (this.props.contentType === 'workbook') {
      icon = <TableauFileWorkbookBaseIcon size={24} />;
      text = Messages.workbookPlaceholder();
    } else if (this.props.contentType === 'view' || this.props.contentType === 'recommendedView') {
      icon = <TableauFileViewBaseIcon size={24} />;
      text = Messages.viewPlaceholder();
    }

    // Renders on top of a successfully loaded thumbnail as a branding-style watermark in dark mode —
    // it is not a "no thumbnail" placeholder despite the visual similarity.
    // The watermark is part of the viewer-controlled VizArea feature: it shows only when that feature is on
    // AND the viewer's viz area is actually dark (viz theme is not Default; Default renders light). So we
    // gate positively on the flag — matching the sibling ViewerControlledThemeSetting — instead of relying on
    // the server coercing `viewerControlledVizTheme` to 'DEFAULT' when the feature is off. With the flag off
    // the viz area is always light, so there is no dark badge. `vizTheme` may be absent (older server / no
    // session yet); `?.` treats absent as "not default" so a dark viz area whose field is unpopulated still
    // shows, and we never throw.
    return this.state.loadThumbnailImage && icon && text && this.state.theme === 'tds-dark'
      && FeatureFlags.viewerControlledThemes_VizAreaEnabled && this.state.vizTheme?.toLowerCase() !== VIZ_THEME_DEFAULT
      ? <>
        <div aria-hidden={true} className={CardViewStyles.DarkModeThumbnailOverlay} />
        <div aria-hidden={true} className={CardViewStyles.DarkModeThumbnailWatermark}>
          {icon}
          <span className={CardViewStyles.DarkModeThumbnailWatermarkText}>{text}</span>
        </div>
      </>
      : <></>;
  }

  public render() {
    if (this.props.navigateUrl && !this.props['aria-label']) {
      throw new Error(`aria-label must be provided in order to meet accessibility requirements `);
    }

    //const imageUri = this.props.useBase64EncodedImage ? `data:image/svg+xml;base64,${this.encodedSvg}` : this.props.thumbnailImageUrl;
    const imageUri = this.thumbnailImageBase64Data || this.props.thumbnailImageUrl;
    const backgroundImageUrl = this.state.loadThumbnailImage ? imageUri : this.props.defaultImageUrl;

    const focusClass = this.props.customShape ? CustomBorderAndFocusClass : CardViewStyles.ThumbnailFocus;

    const classNames = classes(focusClass,
      !this.props.customShape && (this.state.loadThumbnailImage ? CardViewStyles.ThumbnailPreloaded : CardViewStyles.Thumbnail), {
      [CardViewStyles.CardSelectionHighlight]: this.props.selected,
      [CardViewStyles.CardHoverHighlight]: this.props.hovered && !this.props.selected,
    });

    const loadedImageOrCoverBackground = this.props.imageCoverBackground || this.state.loadThumbnailImage;

    const showDefaultNode = !!this.props.defaultImageNode && !this.state.loadThumbnailImage;

    const thumbnailStyle: React.CSSProperties = {
      backgroundImage: showDefaultNode ? undefined : `url(${backgroundImageUrl})`,
      backgroundSize: loadedImageOrCoverBackground ? 'cover' : '',
      height: this.props.customHeight,
    };

    // The outer wrapper needs `position: relative` so absolute-positioned children (e.g. the dark
    // mode overlay) anchor to it. For customShape, positioning comes from the shape's CSS class
    // and `thumbnailStyle` is reused on an inner absolute-positioned div, so we don't add it there.
    const outerThumbnailStyle: React.CSSProperties = this.props.customShape
      ? { height: this.props.customHeight }
      : { ...thumbnailStyle, position: 'relative' };

    return telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) => {
      return this.props.navigateUrl ?
        <div data-tb-test-id={ThumbnailTestId} style={{ zoom: this.state.cardZoomPercentage }}>
          <a href={this.props.navigateUrl}
            target={this.props.openLinkInNewTab ? '_blank' : null}
            rel="noopener noreferrer"
            /**
             * If card reordering allowed, disable link dragging.
             * Otherwise, set `draggable` explicitly to undefined rather than true.
             * (When `draggable` is true, the browser-provided drag avatar looks too similar
             * to the reorder card feature's avatar, which may confuse users.)
             */
            draggable={this.props.allowDragAndDrop ? false : undefined}
            onClick={this.props.onNavigate}
            onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
            className={classNames}
            style={outerThumbnailStyle}
            aria-label={this.props['aria-label']}
          >
            {this.getInnerContent(thumbnailStyle)}
            {this.buildThumbnailOverlayDiv()}
            {this.buildDarkModeOverlay()}
          </a>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div> :
        <div data-tb-test-id={ThumbnailTestId} style={{ zoom: this.state.cardZoomPercentage }}>
          <div
            className={classNames}
            style={outerThumbnailStyle}
          >
            {this.getInnerContent(thumbnailStyle)}
            {this.buildThumbnailOverlayDiv()}
            {this.buildDarkModeOverlay()}
          </div>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div>;
    });
  }

  private getInnerContent = (thumbnailStyle: React.CSSProperties) => {
    /**
     * This may look silly, but it is purposeful. We are passing in both the SVG for the actual card AND
     * we are providing a clip path of the same shape. The clip path is used to clip the background image 
     * and the overlay shown on hover/selection to match the custom shape here (e.g. the folder shape for 
     * projects). The background image is purposefully a separate div and not just the background of the 
     * SVG because it was not performant enough as the background of the SVG (and not supported).
     */
    if (this.props.customShape) {
      return <>
        {this.props.customShape.customShape}
        {this.props.customShape.clipPath}
        <div style={thumbnailStyle} className={classes(this.props.customShape.clippingClass, CustomShapeBackgroundImageClass)} />
      </>;
    }

    // Recompute identically to render()'s `showDefaultNode` (kept in sync there) so the centered node
    // renders exactly when the background is suppressed — avoiding both a double glyph and an empty box.
    const showDefaultNode = !!this.props.defaultImageNode && !this.state.loadThumbnailImage;

    return <>
      {this.props.centeredText && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.centeredText}</div>}
      {showDefaultNode && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.defaultImageNode}</div>}
    </>;
  };
}

// ----------- Custom Shape Helpers and Styles -----------
const CustomShapeBackgroundImageClass = cssClass(cssPrefix, 'shape-background-image', {
  width: TOTAL_CARD_WIDTH,
  position: 'absolute',
  top: 0,
  zIndex: -1,
});

/**
 * See: `ProjectCard::ProjectCardShape` for more details.
 */
const CustomBorderAndFocusClass = cssClass(cssPrefix, 'border-and-focus', {
  display: 'block',
  $nest: {
    '& svg': {
      fill: 'none',
      stroke: applyColor(DesignTokens.color.elevation.border.default.default, '#d8d8d8'),
      strokeWidth: 1,
    },
    '&:focus-visible svg': {
      stroke: applyColor(DesignTokens.color.utility.focus.default.default, CommonStyles.Colors.FocusBorderBlue),
      strokeWidth: 2,
    },
  }
});

export interface CustomShape {
  /** SVG for card */
  customShape: React.ReactElement;
  /** Clip path SVG so that background image and overlay can be clipped */
  clipPath: React.ReactElement;
  /** ID for `clipPath` def */
  clipPathId: string;
  /** Class applying `clipPath` styles */
  clippingClass: string;
}

export function makeCustomThumbnailShape(drawPath: string, clipPathId: string = generateUniqueId('clip-path')): CustomShape {
  const shapeSVG = <svg style={{ width: TOTAL_CARD_WIDTH, height: 'inherit' }}><path d={drawPath} /></svg>;
  const clipPathSVG = <svg style={{ width: 0, height: 0 }}>
    <defs>
      <clipPath id={clipPathId}>
        <path d={drawPath} />
      </clipPath>
    </defs>
  </svg>;

  /**
   * These clipping styles will be applied to the background image and the overlay shown on hover/selection,
   * so it can match the custom shape passed via the `customShape` props.
   */
  const clippingClass = cssClass('Thumbnail', clipPathId, {
    clipPath: `url(#${clipPathId})`,
    // styles below added for Safari, without this does not properly clip shapes after hover or on scroll
    // https://stackoverflow.com/questions/41860477/why-doesnt-css-clip-path-with-svg-work-in-safari
    ...(BrowserSupport.IsSafari() && {
      '-webkit-clip-path': `url(#${clipPathId})`,
      transform: 'translateZ(0)',
    }),
  });

  return ({
    clipPath: clipPathSVG,
    clipPathId,
    clippingClass,
    customShape: shapeSVG,
  });
}